import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Table,
  Pagination,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { Formik } from 'formik';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  FaFileExcel,
  FaSearch,
  FaEraser,
  FaFilter,
  FaCity,
  FaMapMarkedAlt,
  FaExclamationCircle,
  FaSortAmountDown,
} from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import AuthStorage from '~/Utils/AuthStorage';
import { selectStylesSm } from '~/Styles/reactSelectStyles';
import { relatorioCidadesSchema } from '~/validationSchemas';
import { listarCidadesPorRegiao, listarRegioes } from '~/Services/Service';
import { formatarCep, formatarTimestamp, exportarEmExcel } from '~/Utils/Helpers';
import './style.css';

const ITENS_POR_PAGINA = 20;

const OPCOES_ORDEM = [
  { value: 'codigo', label: 'Código' },
  { value: 'descricao', label: 'Nome' },
  { value: 'estado-descricao', label: 'Estado + Nome' },
];

const RelatorioCidadesPorRegiao = () => {
  const [regioes, setRegioes] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [carregando, setCarregando] = useState(false);
  const [regiaoSelecionada, setRegiaoSelecionada] = useState(null);

  // Ref para rastrear se já houve uma busca (para auto-re-fetch ao trocar ordem)
  const codigoRegiaoRef = useRef(null);

  const razaoSocial = (() => {
    const user = AuthStorage.getUser();
    return user && user.razao_social ? user.razao_social : '';
  })();

  // Busca regiões no mount — anti-race (LICOES ciclo 9 L3)
  useEffect(() => {
    let ativo = true;
    listarRegioes()
      .then((res) => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : (res && res.data ? res.data : []);
        setRegioes(lista);
      })
      .catch(() => {});
    return () => { ativo = false; };
  }, []);

  const opcoesRegioes = regioes.map((r) => ({
    value: r.codigo_regiao != null ? r.codigo_regiao : r.codigo,
    label: r.descricao_regiao || r.descricao || '',
  }));

  const totalPaginas = Math.max(1, Math.ceil(cidades.length / ITENS_POR_PAGINA));
  const inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
  const cidadesPagina = cidades.slice(inicio, inicio + ITENS_POR_PAGINA);

  const executarBusca = useCallback(async (codigoRegiao, ordem, optRegioes) => {
    setCarregando(true);
    setCidades([]);
    setPaginaAtual(1);
    const optR = optRegioes.find((o) => o.value === Number(codigoRegiao));
    setRegiaoSelecionada(optR || null);
    try {
      // MEMORY.md §Shared Decisions: rota real é /cidades/relatorio (não /cidades)
      const res = await listarCidadesPorRegiao({ codigoRegiao, ordem });
      const lista = Array.isArray(res) ? res : (res && res.data ? res.data : []);
      setCidades(lista);
    } catch (err) {
      const mensagem = err && err.message ? err.message : 'Erro ao buscar cidades.';
      toast.error(mensagem);
    } finally {
      setCarregando(false);
    }
  }, []);

  const handleSubmit = async (values) => {
    codigoRegiaoRef.current = values.codigoRegiao;
    await executarBusca(values.codigoRegiao, values.ordem, opcoesRegioes);
  };

  const handleOrdemChange = (valor, setFieldValue) => {
    setFieldValue('ordem', valor);
    setPaginaAtual(1);
    // Auto-re-fetch ao trocar ordem se já existe resultado
    if (codigoRegiaoRef.current) {
      executarBusca(codigoRegiaoRef.current, valor, opcoesRegioes);
    }
  };

  const handleLimpar = (resetForm) => {
    resetForm();
    setCidades([]);
    setPaginaAtual(1);
    setRegiaoSelecionada(null);
    codigoRegiaoRef.current = null;
  };

  const handleExportarExcel = (values) => {
    const dataHora = new Date().toLocaleString('pt-BR');
    const nomeRegiao = regiaoSelecionada ? regiaoSelecionada.label : '';

    const linhasMetadados = [
      [razaoSocial],
      ['Relatório de Cidades por Região'],
      [`Gerado em ${dataHora} — Região: ${nomeRegiao}`],
      [],
      ['Código', 'Descrição', 'Estado', 'DDD', 'CEP Inicial', 'CEP Final'],
    ];

    const linhasDados = cidades.map((c) => [
      c.codigo_cidade,
      c.descricao_cidade,
      c.estado,
      c.ddd ? String(c.ddd).padStart(3, '0') : '',
      formatarCep(c.cep_inicial),
      formatarCep(c.cep_final),
    ]);

    const nomeArquivo = `cidades-por-regiao-${values.codigoRegiao}-${formatarTimestamp(new Date())}.xlsx`;
    exportarEmExcel([...linhasMetadados, ...linhasDados], nomeArquivo);
  };

  const renderPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const maxBtn = 5;
    let start = Math.max(1, paginaAtual - Math.floor(maxBtn / 2));
    const end = Math.min(totalPaginas, start + maxBtn - 1);
    if (end - start < maxBtn - 1) start = Math.max(1, end - maxBtn + 1);

    const items = [];
    items.push(
      <Pagination.First key="first" disabled={paginaAtual === 1} onClick={() => setPaginaAtual(1)} />,
      <Pagination.Prev key="prev" disabled={paginaAtual === 1} onClick={() => setPaginaAtual(paginaAtual - 1)} />
    );
    if (start > 1) items.push(<Pagination.Ellipsis key="ell-s" disabled />);
    for (let i = start; i <= end; i++) {
      const pg = i;
      items.push(
        <Pagination.Item key={pg} active={pg === paginaAtual} onClick={() => setPaginaAtual(pg)}>
          {pg}
        </Pagination.Item>
      );
    }
    if (end < totalPaginas) items.push(<Pagination.Ellipsis key="ell-e" disabled />);
    items.push(
      <Pagination.Next key="next" disabled={paginaAtual === totalPaginas} onClick={() => setPaginaAtual(paginaAtual + 1)} />,
      <Pagination.Last key="last" disabled={paginaAtual === totalPaginas} onClick={() => setPaginaAtual(totalPaginas)} />
    );

    return (
      <div className="rcr-pagination-wrap">
        <span className="rcr-pagination-info">
          Mostrando <strong>{cidades.length === 0 ? 0 : Math.min(inicio + 1, cidades.length)}</strong> até{' '}
          <strong>{Math.min(inicio + ITENS_POR_PAGINA, cidades.length)}</strong> de{' '}
          <strong>{cidades.length}</strong> registros
        </span>
        <Pagination size="sm" className="mb-0">{items}</Pagination>
      </div>
    );
  };

  return (
    <Container fluid className="px-3 py-3 rcr-relatorio">
      <Header title="Relatório de Cidades por Região" />
      {razaoSocial && (
        <p className="rcr-page-subtitle">{razaoSocial}</p>
      )}

      <Formik
        initialValues={{ codigoRegiao: '', ordem: 'codigo' }}
        validationSchema={relatorioCidadesSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue, handleSubmit: formikSubmit, resetForm, isSubmitting }) => (
          <form onSubmit={formikSubmit}>
            <div className="rcr-section">
              <div className="rcr-section__header">
                <h6 className="rcr-section__title">
                  <FaFilter className="rcr-icon" />
                  Filtros
                </h6>
              </div>
              <div className="rcr-section__body">
                <Row className="align-items-start">
                  <Col xs={12} md={5} className="mb-3 mb-md-0">
                    <Form.Label className="rcr-label">
                      Região<span className="rcr-required">*</span>
                    </Form.Label>
                    <Select
                      options={opcoesRegioes}
                      value={opcoesRegioes.find((o) => o.value === Number(values.codigoRegiao)) || null}
                      onChange={(opt) => {
                        setFieldValue('codigoRegiao', opt ? opt.value : '');
                        setPaginaAtual(1);
                      }}
                      styles={selectStylesSm}
                      placeholder="Selecione a região..."
                      isClearable
                    />
                    {touched.codigoRegiao && errors.codigoRegiao && (
                      <div className="rcr-error">
                        <FaExclamationCircle size={12} />
                        {errors.codigoRegiao}
                      </div>
                    )}
                  </Col>
                  <Col xs={12} md={4} className="mb-3 mb-md-0">
                    <Form.Label className="rcr-label">
                      <FaSortAmountDown className="mr-1" size={12} />
                      Ordenar por
                    </Form.Label>
                    <div className="rcr-radios">
                      {OPCOES_ORDEM.map((op) => (
                        <Form.Check
                          key={op.value}
                          inline
                          type="radio"
                          id={`ordem-${op.value}`}
                          label={op.label}
                          value={op.value}
                          checked={values.ordem === op.value}
                          onChange={() => handleOrdemChange(op.value, setFieldValue)}
                        />
                      ))}
                    </div>
                  </Col>
                  <Col xs={12} md={3}>
                    <Form.Label className="rcr-label" aria-hidden="true" style={{ visibility: 'hidden' }}>
                      &nbsp;
                    </Form.Label>
                    <div className="d-flex">
                      <Button
                        type="submit"
                        size="sm"
                        variant="primary"
                        className="mr-2 rcr-btn-acao"
                        disabled={isSubmitting || carregando}
                      >
                        <FaSearch className="mr-1" />
                        Gerar
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline-secondary"
                        className="rcr-btn-acao"
                        onClick={() => handleLimpar(resetForm)}
                      >
                        <FaEraser className="mr-1" />
                        Limpar
                      </Button>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>

            {carregando && <Loading />}

            {!carregando && cidades.length > 0 && (
              <div className="rcr-section">
                <div className="rcr-section__header">
                  <h6 className="rcr-section__title">
                    <FaCity className="rcr-icon" />
                    Resultado
                    <span className="rcr-badge-count">
                      {cidades.length} {cidades.length === 1 ? 'cidade' : 'cidades'}
                    </span>
                  </h6>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        Exporta todas as {cidades.length} cidades (não apenas a página atual)
                      </Tooltip>
                    }
                  >
                    <Button
                      size="sm"
                      variant="success"
                      onClick={() => handleExportarExcel(values)}
                    >
                      <FaFileExcel className="mr-1" />
                      Exportar Excel
                    </Button>
                  </OverlayTrigger>
                </div>

                {/* Tabela — visível a partir de sm (≥576px) */}
                <div className="d-none d-sm-block">
                  <Table striped hover size="sm" className="rcr-table mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '90px' }}>Código</th>
                        <th>Descrição</th>
                        <th style={{ width: '80px' }} className="text-center">Estado</th>
                        <th style={{ width: '70px' }} className="text-center">DDD</th>
                        <th style={{ width: '130px' }}>CEP Inicial</th>
                        <th style={{ width: '130px' }}>CEP Final</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cidadesPagina.map((cidade) => (
                        <tr key={cidade.codigo_cidade}>
                          <td className="rcr-td-codigo">{cidade.codigo_cidade}</td>
                          <td>{cidade.descricao_cidade}</td>
                          <td className="rcr-td-estado">{cidade.estado}</td>
                          <td className="text-center rcr-td-mono">
                            {cidade.ddd ? String(cidade.ddd).padStart(3, '0') : '—'}
                          </td>
                          <td className="rcr-td-mono">{formatarCep(cidade.cep_inicial) || '—'}</td>
                          <td className="rcr-td-mono">{formatarCep(cidade.cep_final) || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {renderPaginacao()}
                </div>

                {/* Card stack — visível apenas em xs (≤576px) */}
                <div className="d-sm-none p-2">
                  {cidadesPagina.map((cidade) => (
                    <Card key={cidade.codigo_cidade} className="mb-2 rcr-card-mobile">
                      <Card.Body>
                        <div className="rcr-cm-row">
                          <span className="rcr-cm-label">Código</span>
                          <span className="rcr-cm-value rcr-td-codigo">{cidade.codigo_cidade}</span>
                        </div>
                        <div className="rcr-cm-row">
                          <span className="rcr-cm-label">Descrição</span>
                          <span className="rcr-cm-value">{cidade.descricao_cidade}</span>
                        </div>
                        <div className="rcr-cm-row">
                          <span className="rcr-cm-label">Estado</span>
                          <span className="rcr-cm-value">{cidade.estado}</span>
                        </div>
                        <div className="rcr-cm-row">
                          <span className="rcr-cm-label">DDD</span>
                          <span className="rcr-cm-value rcr-td-mono">
                            {cidade.ddd ? String(cidade.ddd).padStart(3, '0') : '—'}
                          </span>
                        </div>
                        <div className="rcr-cm-row">
                          <span className="rcr-cm-label">CEP Inicial</span>
                          <span className="rcr-cm-value rcr-td-mono">{formatarCep(cidade.cep_inicial) || '—'}</span>
                        </div>
                        <div className="rcr-cm-row">
                          <span className="rcr-cm-label">CEP Final</span>
                          <span className="rcr-cm-value rcr-td-mono">{formatarCep(cidade.cep_final) || '—'}</span>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                  {renderPaginacao()}
                </div>
              </div>
            )}

            {!carregando && cidades.length === 0 && codigoRegiaoRef.current && (
              <div className="rcr-empty">
                <FaMapMarkedAlt className="rcr-empty__icon" />
                <div className="rcr-empty__title">Nenhuma cidade encontrada</div>
                <p className="rcr-empty__subtitle">
                  Não há cidades cadastradas nesta região.
                </p>
              </div>
            )}
          </form>
        )}
      </Formik>
    </Container>
  );
};

export default RelatorioCidadesPorRegiao;
